import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { Route } from '@/constants';
import {
    useCreatePrePurchaseMutation,
    useCreatePurchasesMutation,
    useUpdatePrePurchaseMutation,
} from '@/features/slice/purchases';
import {
    PurchaseFormSchema,
    TPurchaseForm,
    calculatePurchaseTotal,
    mapPurchasePayload,
} from '@/modules/purchase/shared';
import { formatCurrencyInBDT, toastError } from '@/utils';

import { TCreatePrePurchaseForm } from './create-pre-purchase.schema';

export const useCreatePrePurchase = () => {
    const [createPrePurchase, { isLoading }] = useCreatePrePurchaseMutation();
    const [updatePrePurchase, { isLoading: isUpdating }] =
        useUpdatePrePurchaseMutation();

    const params = useParams<{ id: string }>();
    const prePurchaseId = params['id'] || '';

    const [createPurchase, { isLoading: isCreatingPurchase }] =
        useCreatePurchasesMutation();

    const navigate = useNavigate();

    const handleSubmit = async (
        payload: TCreatePrePurchaseForm,
        isEditMode: boolean,
    ) => {
        if (isEditMode) {
            if (!prePurchaseId) {
                toast.error('Pre-purchase ID is missing for update.');
                return;
            }

            if (payload.brand_id === null || payload.brand_id === undefined) {
                toast.error('Brand is required.');
                return;
            }

            try {
                const body = {
                    id: prePurchaseId,
                    supplier_id: payload.supplier_id,
                    note: payload.note,
                    items:
                        payload?.items?.map((item) => ({
                            product_variation_id: item.product_variation_id,
                            quantity: item.quantity,
                            purchase_price: item.purchase_price,
                            wholesale_price: item.wholesale_price,
                            retail_price: item.retail_price,
                            expiration_date: item.expiration_date,
                        })) || [],
                    brand_id: payload.brand_id,
                };

                await updatePrePurchase(body).unwrap();
                toast.success('Pre-purchase updated successfully.');
                navigate(Route.PRE_PURCHASES);
            } catch (error) {
                toastError(error, 'useCreatePrePurchase: handleSubmit');
            }
            return;
        }

        const body = {
            supplier_id: payload.supplier_id,
            note: payload.note,
            items:
                payload?.items?.map((item) => ({
                    product_variation_id: item.product_variation_id,
                    quantity: item.quantity,
                    purchase_price: item.purchase_price,
                    wholesale_price: item.wholesale_price,
                    retail_price: item.retail_price,
                    expiration_date: item.expiration_date,
                })) || [],
            brand_id: payload.brand_id,
        };

        try {
            const response = await createPrePurchase(body).unwrap();

            const prePurchaseId = response.data?.pre_purchase_id;

            navigate(Route.PRE_PURCHASES);

            toast.success('Pre-purchase created successfully.', {
                action: {
                    label: 'View Pre-Purchase',
                    onClick: () => {
                        navigate(`${Route.PRE_PURCHASES}/${prePurchaseId}`);
                    },
                },
            });
        } catch (error) {
            toastError(error, 'useCreatePrePurchase: handleSubmit');
        }
    };

    const handleSubmitPurchase = async (
        payload: TCreatePrePurchaseForm,
        prePurchaseId?: string,
    ) => {
        const purchaseForm = PurchaseFormSchema.safeParse({
            ...payload,
            items: payload.items ?? [],
            total_paid_amount: payload.total_paid_amount ?? 0,
        });

        if (!purchaseForm.success) {
            toast.error(
                'Complete the supplier and all item details before converting to a purchase.',
            );
            return;
        }

        const validatedPayload: TPurchaseForm = purchaseForm.data;
        const totalPurchasePrice = calculatePurchaseTotal(
            validatedPayload.items,
        );

        if (
            totalPurchasePrice < Math.round(validatedPayload.total_paid_amount)
        ) {
            toast.error(
                `Total paid amount must be less than total purchase price of ${formatCurrencyInBDT(totalPurchasePrice)}.`,
            );
            return;
        }

        const body = mapPurchasePayload(validatedPayload, prePurchaseId);

        try {
            await createPurchase(body).unwrap();
            navigate(Route.PURCHASES);
            toast.success('Purchase created successfully.');
        } catch (error) {
            toastError(error, 'useCreatePurchase: handleSubmit');
        }
    };

    return {
        handleSubmit,
        isSaving: isLoading || isUpdating,
        handleSubmitPurchase,
        isCreatingPurchase,
    };
};
